import { forgottenPasswordStore } from "@stores/forgotten-password";
import { isEmail } from "@common/utils/string";
import { useRouter } from "next/router";
import { useEffect, useCallback, ReactElement } from "react";
import { useForm, type UseFormRegister } from "react-hook-form";
import { persistUTMQueryParamsToUrlObject } from "@common/utils/router";
import {
  Anchor,
  Box,
  Button,
  Card,
  Center,
  Stack,
  Text,
  TextInput,
  useMantineTheme,
} from "@mantine/core";
import Link from "next/link";
import CenteredLayout from "@common/components/thirdParty/mantine/layout/CenteredLayout";
import ErrorMessage from "@common/components/text/ErrorMessage";
import Form from "@common/components/ui/form/Form";
import PageTitle from "@common/components/thirdParty/mantine/text/PageTitle";
import { useAccount } from "@stores/session/hooks";
import { IDataFetcherResponseError } from "@common/utils/use-query/types";

interface IForgotPasswordForm {
  email: string;
}

type ForgotPasswordErrorLike = IDataFetcherResponseError | null | undefined;

function ForgotPasswordCardHeader({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <Box p="xl">
      <Stack spacing="xs">
        <PageTitle m={0}>{title}</PageTitle>
        <Text c="gray.4" fz="sm" lh="20px" component="p" my={0}>
          {description}
        </Text>
      </Stack>
    </Box>
  );
}

function ForgotPasswordRequestCard({
  error,
  isLoading,
  isValid,
  onResetErrors,
  onSubmit,
  register,
  footerHref,
}: {
  error: ForgotPasswordErrorLike;
  isLoading: boolean;
  isValid: boolean;
  onResetErrors: () => void;
  onSubmit: () => void;
  footerHref: ReturnType<typeof persistUTMQueryParamsToUrlObject>;
  register: UseFormRegister<IForgotPasswordForm>;
}) {
  return (
    <Card
      withBorder
      radius="md"
      shadow="sm"
      p={0}
      style={{ overflow: "hidden" }}
      w="100%"
    >
      <ForgotPasswordCardHeader
        title="Forgot your password?"
        description="It happens to everyone. Enter the email address associated with your Metabase Cloud account and we'll send you a link to reset your password."
      />

      <Box px="xl" pb="xl">
        <Form onSubmit={onSubmit}>
          <Stack spacing="xl">
            <TextInput
              id="email"
              type="email"
              label="Email address"
              placeholder="almost-there@company.com"
              onInput={() => onResetErrors()}
              {...register("email", {
                required: true,
                validate(value) {
                  return isEmail(value) || "Invalid email";
                },
              })}
            />

            {error && error.message !== "404" && <ErrorMessage error={error} />}

            <Button
              fullWidth
              size="md"
              type="submit"
              disabled={!isValid || isLoading}
              loading={isLoading}
            >
              Send email
            </Button>
          </Stack>
        </Form>
      </Box>

      <ForgotPasswordCardFooter href={footerHref} />
    </Card>
  );
}

function ForgotPasswordSuccessCard({
  email,
  footerHref,
}: {
  email: string;
  footerHref: ReturnType<typeof persistUTMQueryParamsToUrlObject>;
}) {
  return (
    <Card
      withBorder
      radius="md"
      shadow="sm"
      p={0}
      style={{ overflow: "hidden" }}
      w="100%"
    >
      <Box p="xl">
        <Stack align="center" spacing="sm">
          <PageTitle m={0} ta="center" lh="28px">
            Check your inbox
          </PageTitle>
          <Text c="gray.4" fz="sm" ta="center" lh="20px" component="p" my={0}>
            If an account with this email exists, we will send an email to{" "}
            {email} with instructions on how to reset your password.
          </Text>
        </Stack>
      </Box>

      <ForgotPasswordCardFooter href={footerHref} />
    </Card>
  );
}

function ForgotPasswordCardFooter({
  href,
}: {
  href: ReturnType<typeof persistUTMQueryParamsToUrlObject>;
}) {
  const { colors } = useMantineTheme();

  return (
    <Center p="xl" sx={{ borderTop: `1px solid ${colors.gray[3]}` }}>
      <Text c="gray.4" fz="sm" ta="center">
        <Anchor component={Link} href={href}>
          Back to login
        </Anchor>
      </Text>
    </Center>
  );
}

export default function ForgotPasswordPage(): ReactElement {
  const router = useRouter();
  const account = useAccount();

  const {
    mutate: sendResetEmail,
    isPending,
    isSuccess,
    error,
  } = forgottenPasswordStore.useSendResetEmail();

  useEffect(() => {
    if (account) {
      router.replace(persistUTMQueryParamsToUrlObject(router, "/account"));
    }
  }, [account, router]);

  const {
    register,
    getValues,
    formState: { isValid },
    handleSubmit,
    clearErrors,
  } = useForm<IForgotPasswordForm>({
    shouldFocusError: true,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const handleEmailSubmit = useCallback(
    (formData: IForgotPasswordForm) => {
      sendResetEmail(formData);
    },
    [sendResetEmail],
  );

  const backToLoginHref = persistUTMQueryParamsToUrlObject(router, "/login");

  return (
    <CenteredLayout.New wordmark>
      {isSuccess ? (
        <ForgotPasswordSuccessCard
          email={getValues("email")}
          footerHref={backToLoginHref}
        />
      ) : (
        <ForgotPasswordRequestCard
          error={error}
          isLoading={isPending}
          isValid={isValid}
          onResetErrors={clearErrors}
          onSubmit={handleSubmit(handleEmailSubmit)}
          footerHref={backToLoginHref}
          register={register}
        />
      )}
    </CenteredLayout.New>
  );
}

ForgotPasswordPage.hideNav = true;
